/**
 * @fileoverview File Validation Utilities for Upload Security
 *
 * This module provides comprehensive file validation for the webchat
 * file upload feature, including:
 * - MIME type validation using browser-provided metadata
 * - File extension whitelist checking
 * - File size limits (10MB default)
 * - Filename sanitization
 *
 * **Security Features:**
 * - Validates actual file content, not just extension
 * - Prevents upload of potentially malicious files
 * - Whitelist approach for allowed file types
 *
 * **Supported File Types:**
 * - Documents: .doc, .docx, .dotx, .pdf, .xls, .xlsx, .json
 * - Images: .jpg, .jpeg, .png, .gif, .svg, .jfif, .heic
 * - Video: .mov
 *
 * @module helpers/validateFile
 * @author KeyReply Engineering Team
 *
 * @techdebt
 * - Uses lodash instead of lodash-es (inconsistent with other files)
 * - 10MB limit is hardcoded - should be configurable
 * - Error messages are hardcoded - should be i18n-ready
 * - hasSingleExtension and noSpecialCharacter are defined but not used in main validation
 *
 * @security
 * - Uses browser-reported MIME type plus extension allowlist
 * - Extension spoofing is still possible; server-side validation recommended
 */

import _ from "lodash";
import * as Sentry from "@sentry/browser";

/**
 * Validate multiple files for upload
 *
 * Iterates through all files and validates each one, collecting
 * all errors from all files.
 *
 * @param {Array<{name: string, size: number}>} files - Array of file objects to validate
 * @returns {Promise<{isValid: boolean, errors: string[]}>} Validation result
 *
 * @example
 * const result = await validateFiles([file1, file2]);
 * if (!result.isValid) {
 *   console.error(result.errors);
 * }
 */
export async function validateFiles(files: File[]): Promise<{ isValid: boolean; errors: string[] }> {
  let overallErrors: string[] = [];
  const validation = [];
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    const { isValid, errors } = await validateFile(file);
    if (errors.length > 0) {
      overallErrors = overallErrors.concat(errors);
    }
    validation.push(isValid);
  }

  const isValid = _.every(validation, (v) => !!v);

  return { isValid, errors: overallErrors };
}

/**
 * Validate a single file for upload
 *
 * Performs comprehensive validation including:
 * 1. MIME type validation (uses browser-provided type)
 * 2. File extension whitelist check
 * 3. File size limit check (10MB)
 *
 * @param {Object} file - File object to validate
 * @param {string} file.name - File name with extension
 * @param {number} file.size - File size in bytes
 * @returns {Promise<{isValid: boolean, errors: string[]}>} Validation result
 *
 * @security Browser MIME type can be spoofed; rely on server validation as source of truth
 */
export async function validateFile(file: File): Promise<{ isValid: boolean; errors: string[] }> {
  const errors = [];
  const fileName = file.name;

  let mimeTypeValid = true;
  try {
    const mimeType = file.type || null;
    mimeTypeValid = hasWhitelistedMimeTypes(mimeType);
    if (!mimeTypeValid) {
      errors.push(
        `Invalid Mime Type: '${fileName}' can’t be uploaded as the mime type is not supported by the system.`
      );
    }
  } catch (error) {
    Sentry.captureException(error);
  }

  const whitelistedExtensionOnly = hasWhitelistedExtensions(fileName);
  if (!whitelistedExtensionOnly) {
    errors.push(
      `Invalid Action: '${fileName}' can’t be uploaded as the file type is not supported by the system.`
    );
  }

  const validFileSize = hasValidFileSize(file);
  if (!validFileSize) {
    errors.push(`Invalid Action: file size greater than 10mb`);
  }

  const isValid = mimeTypeValid && whitelistedExtensionOnly && validFileSize;
  return { isValid, errors };
}

/**
 * Check if file has a whitelisted extension
 *
 * Validates the file extension against a whitelist of allowed types.
 * Uses case-insensitive matching.
 *
 * @param {string} fileName - Full filename including extension
 * @returns {boolean} True if extension is in whitelist
 *
 * @security First line of defense - but can be spoofed, so also validate MIME type
 */
export function hasWhitelistedExtensions(fileName: string): boolean {
  /**
   * Allowed file extensions whitelist
   * @techdebt Should be configurable via settings
   */
  const allowedFileExtensions = [
    ".xls",   // Excel 97-2003
    ".xlsx",  // Excel 2007+
    ".doc",   // Word 97-2003
    ".docx",  // Word 2007+
    ".dotx",  // Word template
    ".jpeg",  // JPEG image
    ".jpg",   // JPEG image (alternate)
    ".png",   // PNG image
    ".pdf",   // PDF document
    ".svg",   // SVG vector image
    ".gif",   // GIF image
    ".json",  // JSON data
    ".jfif",  // JPEG File Interchange Format
    ".heic",  // HEIC image (Apple)
    ".mov",   // QuickTime video
  ];

  // Build regex pattern for case-insensitive extension matching
  const fileExtRegex = new RegExp(
    "(" + allowedFileExtensions.join("|").replace(/\./g, "\\.") + ")$",
    "i"
  );
  const isAllowed = fileExtRegex.test(fileName);

  return isAllowed;
}

/**
 * Check if MIME type is in the whitelist
 *
 * Validates the detected MIME type against allowed types.
 * MIME type should be detected from file content, not trusted from user.
 *
 * @param {string|null} mimeType - MIME type string or null if detection failed
 * @returns {boolean} True if MIME type is allowed
 *
 * @security This validates actual file content type detected by file-type library
 */
export function hasWhitelistedMimeTypes(mimeType: string | null): boolean {
  /**
   * Allowed MIME types whitelist
   * @techdebt Should be configurable via settings
   */
  const allowedMimeTypes = [
    "application/vnd.ms-excel",                                              // .xls
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",     // .xlsx
    "application/msword",                                                    // .doc
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
    "application/vnd.openxmlformats-officedocument.wordprocessingml.template", // .dotx
    "image/jpeg",      // .jpg, .jpeg
    "image/png",       // .png
    "application/pdf", // .pdf
    "image/svg+xml",   // .svg
    "image/gif",       // .gif
    "application/json", // .json
    "image/heic",      // .heic
    "video/quicktime", // .mov
  ];
  return !mimeType ? false : allowedMimeTypes.includes(mimeType);
}

/**
 * Check if filename has only a single extension
 *
 * Detects potential double-extension attacks (e.g., "file.txt.exe").
 *
 * @param {string} fileName - Filename to check
 * @returns {boolean} True if file has exactly one extension
 *
 * @security Prevents double-extension spoofing attacks
 * @techdebt Not currently used in main validation - consider integrating
 */
export function hasSingleExtension(fileName: string): boolean {
  const ext = fileName.split(".");
  const onlyOneExt = ext.length === 2;
  return onlyOneExt;
}

/**
 * Check if filename contains no special characters
 *
 * Validates filename doesn't contain potentially dangerous characters
 * that could cause issues with storage or display.
 *
 * @param {string} fileName - Filename to check
 * @returns {boolean} True if no special characters found
 *
 * @security Prevents path traversal and injection attacks
 * @techdebt Not currently used in main validation - consider integrating
 */
export function noSpecialCharacter(fileName: string): boolean {
  if (!fileName) {
    return true;
  }

  // Characters that could be dangerous in filenames
  const regex = /[`~!@#$%^&*()|+=?;:'",<>{}\\]/gi;
  const haveSpecialCharacter = regex.test(fileName);

  return !haveSpecialCharacter;
}

/**
 * Check if file size is within allowed limit
 *
 * @param {Object} file - File object with size property
 * @param {number} file.size - File size in bytes
 * @param {number} [tenMbLimit=10240] - Size limit in KB (default 10MB)
 * @returns {boolean} True if file is within size limit
 *
 * @techdebt
 * - Parameter name "tenMbLimit" is misleading as it's in KB
 * - Default value 10240 KB = 10 MB, but could be configurable
 */
export function hasValidFileSize(file: { size: number }, tenMbLimit = 10240): boolean {
  const fileSize = file.size;
  const fileSizeInKB = Math.round(fileSize / 1024);

  const fileLessThan10Mb = fileSizeInKB <= tenMbLimit;
  return fileLessThan10Mb;
}

/**
 * Transform filename to remove special characters
 *
 * Replaces special characters in filename with hyphens while
 * preserving the file extension.
 *
 * @param {string} originalFileName - Original filename
 * @returns {string} Sanitized filename with special chars replaced by hyphens
 *
 * @example
 * transformFileName("my file (1).pdf") // Returns: "my-file--1-.pdf"
 */
export function transformFileName(originalFileName: string) {
  let fileExtension = "";
  let fileName = originalFileName;
  const lastIndexOfDot = originalFileName.lastIndexOf(".");

  // Extract extension from filename
  if (lastIndexOfDot >= 0) {
    fileExtension = originalFileName.substring(lastIndexOfDot + 1, originalFileName.length);
    fileName = originalFileName.substring(0, lastIndexOfDot);
  }

  // Replace special characters with hyphens
  const newFileName = fileName.replace(/[`~!@#$%^&*()|+=?;:'",<>.\{\}\[\]\\\/]/gi, "-");
  return `${newFileName}.${fileExtension}`;
}
